import { useCallback, useEffect, useRef, useState } from 'react';
import { useElementWidth } from '../../../../utils/customHooks';
import { createResizeObserver } from '../../../../utils/createResizeObserver';
import {
    BANK_STATEMENTS_HEADER_MIN_WIDTH,
    BANK_STATEMENTS_HEADER_MIN_WIDTH_WITH_ACCOUNT,
    BANK_STATEMENTS_HEADER_MIN_WIDTH_WITH_BACK_LABEL,
    BANK_STATEMENTS_HEADER_HEIGHT_VAR,
    CFB_INLINE_RANGES_MIN_WIDTH,
} from './constants';

export function getBankStatementsHeaderFlags(width: number, hasAccount: boolean) {
    const collapseThreshold = hasAccount ? BANK_STATEMENTS_HEADER_MIN_WIDTH_WITH_ACCOUNT : BANK_STATEMENTS_HEADER_MIN_WIDTH;
    return {
        collapseExcelAnalysis: width < collapseThreshold,
        hideBackLabel: width < BANK_STATEMENTS_HEADER_MIN_WIDTH_WITH_BACK_LABEL,
    };
}

export function useBankingHeaderLayout(selectedAccountId: string | null) {
    const moduleRef = useRef<HTMLDivElement>(null);
    const [headerElement, setHeaderElement] = useState<HTMLElement | null>(null);
    const [cfbToolbarRef, cfbToolbarWidth] = useElementWidth();
    const [setHeaderWidthElement, headerWidth] = useElementWidth();

    const bankStatementsHeaderRef = useCallback(
        (element: HTMLElement | null) => {
            setHeaderWidthElement(element);
            setHeaderElement(element);
        },
        [setHeaderWidthElement],
    );

    useEffect(() => {
        if (!headerElement) return undefined;
        const writeHeaderHeight = () =>
            moduleRef.current?.style.setProperty(BANK_STATEMENTS_HEADER_HEIGHT_VAR, `${headerElement.offsetHeight}px`);
        writeHeaderHeight();
        const observer = createResizeObserver(writeHeaderHeight);
        if (!observer) return undefined;
        observer.observe(headerElement);
        return () => observer.disconnect();
    }, [headerElement]);

    const hasMeasuredHeader = headerWidth > 0;
    const headerFlags = getBankStatementsHeaderFlags(headerWidth, !!selectedAccountId);
    return {
        cfbToolbarRef,
        bankStatementsHeaderRef,
        moduleRef,
        collapseQuickRanges: cfbToolbarWidth > 0 && cfbToolbarWidth < CFB_INLINE_RANGES_MIN_WIDTH,
        collapseExcelAnalysis: hasMeasuredHeader && headerFlags.collapseExcelAnalysis,
        hideBackLabel: hasMeasuredHeader && headerFlags.hideBackLabel,
    };
}
